import React from 'react';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@/components/ui/tabs';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';



const MonitoringPage = ({
  monitoringSubTab,
  setMonitoringSubTab,
  campaignFilterStatus,
  setCampaignFilterStatus,
  campaignFilterScenario,
  setCampaignFilterScenario,
  contactFilterOrganization,
  setContactFilterOrganization,
  contactFilterStatus,
  setContactFilterStatus,
  filteredMonitoringCampaigns,
  scenarios,
  organizations,
  filteredMonitoringContacts,
  contactInteractions,
  formatTimestamp,
}) => {
  const getScenarioLabel = (scenarioValue) => {
    const found = scenarios.find(s => String(s.value) === String(scenarioValue));
    return found ? found.label : '—';
  };

  return (
    <div className="space-y-6">
      <h2 className="text-3xl font-bold">Monitoring & Analytics</h2>
      <Tabs value={monitoringSubTab} onValueChange={setMonitoringSubTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="campaigns">Campaign Analytics</TabsTrigger>
          <TabsTrigger value="contacts">Contact Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="campaigns">
          <Card>
            <CardHeader>
              <CardTitle>Campaign Performance Overview</CardTitle>
              <CardDescription>Detailed analytics for your outreach campaigns.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex space-x-4 mb-4">
                <Select value={campaignFilterStatus} onValueChange={setCampaignFilterStatus}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Filter by Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Pending Approval">Pending Approval</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={campaignFilterScenario} onValueChange={setCampaignFilterScenario}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Filter by Scenario" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Scenarios</SelectItem>
                    {scenarios.map((s) => (
                      <SelectItem key={s.value} value={String(s.value)}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Campaign Name</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Scenario</TableHead>
                    <TableHead>Total Contacts</TableHead>
                    <TableHead>Replies</TableHead>
                    <TableHead>Opened</TableHead>
                    <TableHead>Bounced</TableHead>
                    <TableHead>No Response</TableHead>
                    <TableHead>Progress</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredMonitoringCampaigns.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center text-muted-foreground">
                        No campaigns found matching your filters.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredMonitoringCampaigns.map((campaign) => (
                      <TableRow key={campaign.id}>
                        <TableCell className="font-medium">{campaign.name}</TableCell>
                        <TableCell>
                          <Badge variant={campaign.status === 'Active' ? 'default' : 'secondary'}>
                            {campaign.status}
                          </Badge>
                        </TableCell>
                        <TableCell>{getScenarioLabel(campaign.scenario)}</TableCell>
                        <TableCell>{campaign.totalContacts}</TableCell>
                        <TableCell>{campaign.replied}</TableCell>
                        <TableCell>{campaign.opened}</TableCell>
                        <TableCell>{campaign.bounced}</TableCell>
                        <TableCell>{campaign.noResponse}</TableCell>
                        <TableCell>
                          <Progress value={campaign.progress} className="w-[100px]" />
                          <span className="ml-2 text-sm text-muted-foreground">{campaign.progress}%</span>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="contacts">
          <Card>
            <CardHeader>
              <CardTitle>Contact Engagement Overview</CardTitle>
              <CardDescription>Detailed analytics for individual contacts.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex space-x-4 mb-4">
                <Select value={contactFilterOrganization} onValueChange={setContactFilterOrganization}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Filter by Organization" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Organizations</SelectItem>
                    {organizations.map((org) => (
                      <SelectItem key={org.id} value={org.name}>{org.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* <Select value={contactFilterStatus} onValueChange={setContactFilterStatus}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Filter by Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="Hot Lead">Hot Lead</SelectItem>
                    <SelectItem value="Engaged">Engaged</SelectItem>
                    <SelectItem value="Pending">Pending</SelectItem>
                  </SelectContent>
                </Select> */}
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Contact Name</TableHead>
                    <TableHead>Organization</TableHead>
                    <TableHead>Total Interactions</TableHead>
                    <TableHead>Replies Received</TableHead>
                    <TableHead>Emails Opened</TableHead>
                    <TableHead>Last Interaction</TableHead>
                    <TableHead>Current Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredMonitoringContacts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-muted-foreground">
                        No contacts found matching your filters.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredMonitoringContacts.map(contact => (
                      <TableRow key={contact.id}>
                        <TableCell className="font-medium">{contact.name}</TableCell>
                        <TableCell>{contact.organizationName}</TableCell>
                        <TableCell>{contact.totalInteractions}</TableCell>
                        <TableCell>{contact.repliesReceived}</TableCell>
                        <TableCell>{contact.emailsOpened}</TableCell>
                        <TableCell>{contact.lastInteraction ? formatTimestamp(contact.lastInteraction) : 'N/A'}</TableCell>
                        <TableCell>
                          <Badge variant={(contact.status || 'N/A').toLowerCase() === 'replied' ? 'default' : 'secondary'}>
                            {contact.status || 'N/A'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MonitoringPage;